/**
 * Which camera this device can actually offer, and how to read a frame off it.
 *
 * There are two ways to take a photo in a browser and each works on exactly the
 * platforms the other does not:
 *
 * - `<input type="file" capture="environment">` hands the job to the operating
 *   system's own camera app. Implemented on **Android and iOS only**. Desktop
 *   browsers -- including Edge and Chrome on a Windows tablet -- **ignore the
 *   attribute silently** and open a file dialog, which is indistinguishable
 *   from the gallery button and is exactly how this looked broken.
 * - `getUserMedia` works everywhere else, and is the only in-app camera a
 *   Windows tablet has.
 *
 * So the button asks `hasNativeCapture()` and takes one road or the other. This
 * is a question about the platform: no media query can answer it, and
 * `useIsTouch` must not be used for it.
 */
import { Capacitor } from '@capacitor/core'

/** Will `capture` on a file input actually open the camera here? */
export function hasNativeCapture () {
  // Our Android build: `BridgeWebChromeClient.onShowFileChooser` routes the
  // attribute to MediaStore.ACTION_IMAGE_CAPTURE. (It also needs the
  // `<queries>` declaration in android_custom/AndroidManifest.xml -- without
  // it the bridge falls back to the file picker and this returns a confident
  // wrong answer.)
  try {
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) return true
  } catch (e) { /* not in a Capacitor context */ }

  if (typeof navigator === 'undefined') return false

  const uad = navigator.userAgentData
  if (uad && typeof uad.mobile === 'boolean') return uad.mobile

  const ua = navigator.userAgent || ''
  if (/Android|iPhone|iPod/i.test(ua)) return true
  // An iPad reports itself as a Mac; the touch points are what give it away.
  if (/iPad/i.test(ua)) return true
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true

  return false
}

/** Can we open a camera stream at all? */
export function hasUserMedia () {
  return !!(typeof navigator !== 'undefined'
    && navigator.mediaDevices
    && navigator.mediaDevices.getUserMedia)
}

/** Is there any way at all to take a photo in place? */
export function canTakePhoto () {
  return hasNativeCapture() || hasUserMedia()
}

// Ask for the sensor rather than settling for what the track offers by
// default. This is the whole point of the rework: the Uppy webcam read a
// plain `getUserMedia` video track, and the median checklist photo in
// production is **84 KB** -- a quarter-megapixel picture of a quality record.
// `ideal` rather than `exact` so a camera that cannot do it still opens.
const WANTED = { width: { ideal: 3840 }, height: { ideal: 2160 } }

// A QR code is read many times a second, and decoding a 4K frame on a shop
// floor tablet takes longer than the operator holds the sticker still. 720p
// reads a sticker from a metre away.
export const SCAN_SIZE = { width: { ideal: 1280 }, height: { ideal: 720 } }

/** Open a camera stream, preferring the rear one and the largest frame.
 *
 * `size` defaults to the photo size; the QR scanner passes `SCAN_SIZE`.
 *
 * A `deviceId` that no longer exists falls back to the rear-facing default
 * instead of failing: ids are reissued when permission is reset, and a
 * remembered one going stale must not read as "this device has no camera". */
export async function openCameraStream (deviceId, size = WANTED) {
  if (deviceId) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId }, ...size },
        audio: false,
      })
    } catch (e) {
      // Only a missing camera is worth retrying. A denied permission is an
      // answer, and retrying it just asks the operator twice.
      if (e?.name !== 'OverconstrainedError' && e?.name !== 'NotFoundError') throw e
    }
  }
  return navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' }, ...size },
    audio: false,
  })
}

/** The camera a stream actually opened.
 *
 * Reopening has to ask for the same camera **by id**. Asking for it by its
 * position in `listCameras()` is what put an operator on the front camera on
 * their second photo: the first open has no list yet, so it goes through
 * `facingMode: environment` and gets the rear camera, and by the second open
 * the list exists and index 0 is whatever `enumerateDevices()` happened to
 * return first -- which on most tablets is the front one. The order carries no
 * meaning and the spec promises none. */
export function streamDeviceId (stream) {
  try {
    return stream?.getVideoTracks?.()[0]?.getSettings?.().deviceId || null
  } catch (e) {
    return null
  }
}

/** Stop every track of a stream. Call it on close AND on unmount.
 *
 * Forgetting one of the two is what left the old camera light on until the
 * page was reloaded: the DOM node went away and nothing told the hardware. */
export function stopCameraStream (stream) {
  if (!stream) return
  try {
    stream.getTracks().forEach(track => track.stop())
  } catch (e) { /* already gone */ }
}

/** The cameras this device has, for the switch button. */
export async function listCameras () {
  if (!navigator.mediaDevices?.enumerateDevices) return []
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter(d => d.kind === 'videoinput')
  } catch (e) {
    return []
  }
}

/** A JPEG `File` of the current frame of `video`.
 *
 * Encoded at 0.92 and at the track's own frame size: nothing here is allowed to
 * shrink the picture, for the same reason nothing shrinks an uploaded one. */
export function grabFrame (video, name) {
  return new Promise((resolve, reject) => {
    const w = video?.videoWidth
    const h = video?.videoHeight
    if (!w || !h) {
      reject(new Error('The camera has no frame yet'))
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d').drawImage(video, 0, 0, w, h)
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not read the frame'))
          return
        }
        resolve(new File([blob], name, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      0.92,
    )
  })
}
